import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { formatMoney, renderMultiline, displayAddress } from '../core/format.js';
import { t } from '../core/i18n.js';
import { toast } from '../components/Toast.js';
import { showModal } from '../components/Modal.js';

export class PublicPropertyView extends Component {
  constructor(ctx) {
    super();
    this.slug = (ctx && ctx.params && ctx.params.slug) || '';
    this.state = { loading: true, property: null, error: null };
  }

  template() {
    if (this.state.loading) {
      return `<section class="stack"><div class="muted">${esc(t('public.loading'))}</div></section>`;
    }
    if (this.state.error) {
      return `
        <section class="stack">
          <div class="card stack">
            <h2>${esc(t('public.unavailable_title'))}</h2>
            <div class="error-banner">${esc(this.state.error)}</div>
          </div>
        </section>
      `;
    }
    const p = this.state.property;
    // Aggregate-aware: collapses to "City, N helyszín" when the published
    // units span >1 distinct address (backend supplies unit_location_count).
    const addr = esc(displayAddress(p));
    const hasContact = (p.public_contact_email && p.public_contact_email !== '')
      || (p.public_contact_phone && p.public_contact_phone !== '');
    return `
      <section class="stack">
        <div class="card stack">
          <h2>${esc((p.public_title && String(p.public_title).trim()) || p.name)}</h2>
          <div class="faint">${addr}</div>
          ${p.public_description ? `<p class="multiline">${renderMultiline(p.public_description)}</p>` : ''}
        </div>

        <div class="row-between"><h3>${esc(t('public.available_units'))}</h3></div>
        ${(p.units || []).length === 0
          ? `<div class="muted">${esc(t('public.no_units'))}</div>`
          : `<div class="stack">${p.units.map((u) => this._unitRow(u, p.currency)).join('')}</div>`}

        ${hasContact ? `
          <div class="card stack">
            <strong>${esc(t('public.contact'))}</strong>
            ${p.public_contact_email ? `<div>📧 <a href="mailto:${esc(p.public_contact_email)}">${esc(p.public_contact_email)}</a></div>` : ''}
            ${p.public_contact_phone ? `<div>📞 <a href="tel:${esc(p.public_contact_phone)}">${esc(p.public_contact_phone)}</a></div>` : ''}
          </div>` : ''}

        <div class="card stack">
          <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
            <button class="btn btn-primary" data-action="ask">${esc(t('inquiries.ask_cta'))}</button>
          </div>
        </div>
      </section>
    `;
  }

  _unitRow(u, currency) {
    const meta = [esc(u.type)];
    if (u.size_sqm) meta.push(esc(u.size_sqm) + ' m²');
    // Per-unit currency overrides the property default (e.g. an EUR office
    // in an otherwise-HUF building). Backend already resolves the inherit
    // case, so `u.currency` should always be a code here.
    const unitCurrency = u.currency || currency || 'HUF';
    const rent = u.asking_rent != null
      ? `<strong>${esc(formatMoney(u.asking_rent, unitCurrency))}${esc(t('public.per_month_suffix'))}</strong>`
      : `<span class="faint">${esc(t('public.price_on_request'))}</span>`;
    return `
      <div class="list-item">
        <div class="stack-2 grow">
          <strong>${esc(u.identifier)}</strong>
          <span class="faint">${meta.join(' · ')}</span>
        </div>
        <div>${rent}</div>
      </div>
    `;
  }

  mount() {
    if (this.state.property === null && !this.state.error) this._load();
    this.el.querySelector('[data-action="ask"]')?.addEventListener('click', () => this._handleAsk());
  }

  // If the visitor has already opened an inquiry on this listing in this
  // browser, resume that conversation instead of spawning a new one.
  _handleAsk() {
    let existing = null;
    try { existing = localStorage.getItem('nestly.inquiry.' + this.slug); } catch (_) {}
    if (existing) {
      window.location.hash = `#/c/${encodeURIComponent(existing)}`;
      return;
    }
    this._openInquiryModal();
  }

  _openInquiryModal() {
    const u = auth.user;
    const initialName = esc(u?.full_name || '');
    const initialEmail = esc(u?.email || '');
    const body = `
      <form class="stack" data-inq-form>
        <p class="muted" style="margin:0">${esc(t('inquiries.subtitle'))}</p>
        <label class="stack-2"><span>${esc(t('inquiries.name'))}</span>
          <input class="input" type="text" name="name" required maxlength="150" value="${initialName}" />
        </label>
        <label class="stack-2"><span>${esc(t('inquiries.email'))}</span>
          <input class="input" type="email" name="email" required maxlength="254" value="${initialEmail}" />
        </label>
        <label class="stack-2"><span>${esc(t('inquiries.phone'))}</span>
          <input class="input" type="tel" name="phone" maxlength="40" />
        </label>
        <label class="stack-2"><span>${esc(t('inquiries.message'))}</span>
          <textarea class="input" name="message" required rows="5" maxlength="4000"></textarea>
        </label>
      </form>
    `;
    const modal = showModal({
      title: t('inquiries.modal_title'),
      body,
      actions: [
        { label: t('common.cancel'), variant: 'ghost' },
        { label: t('inquiries.send'), variant: 'primary', onClick: () => this._submitInquiry(modal) },
      ],
    });
  }

  async _submitInquiry(modal) {
    const form = modal.el.querySelector('[data-inq-form]');
    if (!form) return false;
    const fd = new FormData(form);
    const payload = {
      name: String(fd.get('name') || '').trim(),
      email: String(fd.get('email') || '').trim(),
      phone: String(fd.get('phone') || '').trim(),
      message: String(fd.get('message') || '').trim(),
    };
    if (!payload.name || !payload.email || !payload.message) {
      toast.error(t('inquiries.errors.required'));
      return false;
    }
    try {
      const res = await api.post(`/public/properties/${encodeURIComponent(this.slug)}/inquiries`, payload);
      const token = res && res.token;
      if (token) {
        try { localStorage.setItem('nestly.inquiry.' + this.slug, token); } catch (_) {}
        window.location.hash = `#/c/${encodeURIComponent(token)}`;
      } else {
        toast.success(t('inquiries.sent_title'));
      }
      return true;
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('inquiries.errors.create'));
      return false;
    }
  }

  async _load() {
    try {
      const property = await api.get(`/public/properties/${encodeURIComponent(this.slug)}`);
      this.setState({ property, loading: false });
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 404
        ? t('public.unavailable_message')
        : (err instanceof ApiError ? err.message : t('public.unavailable_message'));
      this.setState({ error: msg, loading: false });
    }
  }
}
